// src/components/common/PopupHost.js
// Host render untuk sistem popup global (lihat popup.js).
// Cukup dipasang SEKALI di App.js: <PopupHost />
//
// CATATAN PENTING: popup ini SENGAJA TIDAK memakai react-bootstrap <Modal>.
// Banyak aksi (mis. "Selesai perbaikan") dipicu dari DALAM sebuah Bootstrap
// Modal. Bila popup juga berupa Bootstrap Modal, dua modal akan berebut
// backdrop/scroll-lock sehingga popup ikut tersembunyi. Karena itu popup ini
// dibuat sebagai overlay mandiri yang di-portal ke <body> dengan z-index
// sangat tinggi, agar selalu tampil di atas modal apa pun.
import React, { useEffect, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import { popupStore } from "./popup";

const ICON = {
  success: "✅",
  error: "❌",
  warning: "⚠️",
  info: "ℹ️",
};

const BTN_COLOR = {
  success: "#198754",
  error: "#dc3545",
  warning: "#fd7e14",
  info: "#0d6efd",
};

export default function PopupHost() {
  const current = useSyncExternalStore(
    popupStore.subscribe,
    popupStore.getCurrent,
    popupStore.getCurrent,
  );

  const open = !!current;
  const isConfirm = current?.mode === "confirm";
  const type = current?.type || "info";

  // Tutup via backdrop/Esc: confirm = batal (false), alert = OK (true).
  const dismiss = () => popupStore.resolve(isConfirm ? false : true);

  // Tutup dengan tombol Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") dismiss();
      if (e.key === "Enter") popupStore.resolve(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isConfirm]);

  if (!open) return null;

  const accent = BTN_COLOR[type] || BTN_COLOR.info;

  const overlayStyle = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    zIndex: 20000, // di atas Bootstrap modal (±1055)
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
  };

  const cardStyle = {
    background: "#fff",
    borderRadius: 14,
    maxWidth: 420,
    width: "100%",
    padding: "28px 24px 22px",
    textAlign: "center",
    boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
    animation: "popupScaleIn 0.15s ease-out",
  };

  const btnBase = {
    minWidth: 96,
    padding: "8px 18px",
    borderRadius: 8,
    fontWeight: 600,
    cursor: "pointer",
    border: "1px solid transparent",
  };

  return createPortal(
    <div style={overlayStyle} onMouseDown={dismiss}>
      {/* stopPropagation agar klik di dalam kartu tidak menutup popup */}
      <div style={cardStyle} onMouseDown={(e) => e.stopPropagation()}>
        <style>
          {`@keyframes popupScaleIn{from{transform:scale(.9);opacity:0}to{transform:scale(1);opacity:1}}`}
        </style>

        <div style={{ fontSize: "3.25rem", lineHeight: 1 }}>
          {ICON[type] || ICON.info}
        </div>

        {current?.title && (
          <div style={{ fontSize: "1.15rem", fontWeight: 700, marginTop: 14 }}>
            {current.title}
          </div>
        )}

        <div
          style={{
            marginTop: 10,
            fontSize: "1rem",
            color: "#333",
            whiteSpace: "pre-line",
          }}
        >
          {current?.message}
        </div>

        <div
          style={{
            marginTop: 22,
            display: "flex",
            justifyContent: "center",
            gap: 10,
          }}
        >
          {isConfirm && (
            <button
              type="button"
              style={{
                ...btnBase,
                background: "#fff",
                borderColor: "#adb5bd",
                color: "#495057",
              }}
              onClick={() => popupStore.resolve(false)}
            >
              {current?.cancelText || "Batal"}
            </button>
          )}
          <button
            type="button"
            autoFocus
            style={{ ...btnBase, background: accent, color: "#fff" }}
            onClick={() => popupStore.resolve(true)}
          >
            {current?.confirmText || "OK"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
